import appUtils from '@/utils/appUtils'

/**
 * Mixin chứa logic dùng chung cho việc lưu/đọc DoctorAI data vào localStorage.
 *
 * Component sử dụng mixin này phải tự implement:
 *   - getDoctorAIStorageKey(): string  → trả về key localStorage tương ứng
 */
export const mixinDoctorAIStorage = {
  data () {
    return {
      doctorAIProcessingIds: []
    }
  },
  methods: {
    getDoctorAIStorageData () {
      try {
        const key = this.getDoctorAIStorageKey()
        const raw = window.localStorage.getItem(key)
        if (!raw || !appUtils.isJSON(raw)) {
          return { processingIds: [], rawData: {} }
        }
        const data = JSON.parse(raw)
        if (!data || typeof data !== 'object') {
          return { processingIds: [], rawData: {} }
        }
        return {
          processingIds: Array.isArray(data.processingIds) ? data.processingIds : [],
          rawData: data.rawData && typeof data.rawData === 'object' ? data.rawData : {}
        }
      } catch (e) {
        console.error('Error reading doctor AI storage data:', e)
        return { processingIds: [], rawData: {} }
      }
    },
    saveDoctorAIStorageData (processingIds, rawData) {
      const key = this.getDoctorAIStorageKey()
      const storageData = this.getDoctorAIStorageData()
      window.localStorage.setItem(
        key,
        JSON.stringify({
          processingIds: processingIds || storageData.processingIds,
          rawData: rawData || storageData.rawData
        })
      )
    },
    async _doctorAIFetchResponse (contextId, type, typeId, lang, question) {
      if (!contextId || !type || !typeId) return

      const storageData = this.getDoctorAIStorageData()
      const existingContext = storageData.rawData[contextId]
      if (existingContext?.answer) return
      let statusResult = 0

      try {
        const res = await this.$rf.getRequest('DoctorRequest').doctorAiResponse({
          type,
          type_id: typeId,
          lang
        })
        const data = res?.data
        if (res?.status === 200 && data?.status === 'success' && data?.answer) {
          const updatedStorage = this.getDoctorAIStorageData()
          const rawData = {
            question: data.question || question,
            answer: data.answer
          }
          updatedStorage.rawData[contextId] = {
            id: contextId,
            question: data.question || question,
            answer: data.answer,
            history: [...(updatedStorage.rawData[contextId]?.history || []), rawData]
          }
          this.saveDoctorAIStorageData(updatedStorage.processingIds, updatedStorage.rawData)
          statusResult = res?.status || 0
        } else {
          statusResult = await this._doctorAIPreloadSingle(contextId, question)
        }
      } catch (e) {
        console.error('Doctor AI fetch response error:', e)
        statusResult = await this._doctorAIPreloadSingle(contextId, question)
      } finally {
        // eslint-disable-next-line no-unsafe-finally
        return statusResult
      }
    },
    async _doctorAIPreloadSingle (contextId, question) {
      if (!contextId || !question) return

      const self = this
      const storageData = self.getDoctorAIStorageData()
      const mergedIds = [...new Set([...self.doctorAIProcessingIds, ...storageData.processingIds])]
      self.doctorAIProcessingIds = mergedIds

      const existingContext = storageData.rawData[contextId]

      // Đang xử lý cho ID này và chưa có answer → không gửi thêm
      if (self.doctorAIProcessingIds.includes(contextId) && !existingContext?.answer) return
      // Đã có answer → không gửi lại
      if (existingContext?.answer) return

      const history = existingContext?.history || []

      if (!self.doctorAIProcessingIds.includes(contextId)) {
        self.doctorAIProcessingIds.push(contextId)
      }
      storageData.rawData[contextId] = { id: contextId, question, history }
      self.saveDoctorAIStorageData(self.doctorAIProcessingIds, storageData.rawData)

      const body = { lang: self.$lang, question, history }
      let statusResult = 0

      try {
        const res = await self.$rf.getRequest('DoctorRequest').doctorAiAsk(body)
        if (res?.status === 200 && res?.data?.answer) {
          const updatedStorage = self.getDoctorAIStorageData()
          // Xóa khỏi processingIds khi đã có answer
          const updatedProcessingIds = updatedStorage.processingIds.filter(id => id !== contextId)
          const idx = self.doctorAIProcessingIds.indexOf(contextId)
          if (idx > -1) self.doctorAIProcessingIds.splice(idx, 1)
          const newHistoryItem = { question, answer: res.data.answer }
          const updatedHistory = [...(updatedStorage.rawData[contextId]?.history || []), newHistoryItem]
          updatedStorage.rawData[contextId] = {
            id: contextId,
            question,
            answer: res.data.answer,
            history: updatedHistory
          }
          self.saveDoctorAIStorageData(updatedProcessingIds, updatedStorage.rawData)
          statusResult = res?.status || 0
        }
      } catch (e) {
        console.error('Doctor AI preload error:', e)
        const idx = self.doctorAIProcessingIds.indexOf(contextId)
        if (idx > -1) self.doctorAIProcessingIds.splice(idx, 1)
        self.saveDoctorAIStorageData(self.doctorAIProcessingIds, storageData.rawData)
        statusResult = 500
      } finally {
        // eslint-disable-next-line no-unsafe-finally
        return statusResult
      }
    }
  }
}
